import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Button, Box, Typography, LinearProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoginService } from '../api/services/loginService';

const TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours (1 day)
const WARNING_MS = 10 * 60 * 1000; // 10 minutes before timeout

export default function SessionTimeoutWatcher() {
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [showWarning, setShowWarning] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(600);
    const [extending, setExtending] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        dispatch.auth.logoutAsync();
        navigate('/login');
    }, [dispatch, navigate]);

    const resetTimer = useCallback(() => {
        setLastActivity(Date.now());
        if (showWarning) {
            setShowWarning(false);
        }
    }, [showWarning]);

    const handleExtendSession = useCallback(async () => {
        setExtending(true);
        try {
            await LoginService.extendSession();
            // API confirmed session extension — reset local timer
            resetTimer();
        } catch (error) {
            // Session extension failed — force logout
            handleLogout();
        } finally {
            setExtending(false);
        }
    }, [resetTimer, handleLogout]);

    useEffect(() => {
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetTimer));

        const checkInterval = setInterval(() => {
            const now = Date.now();
            const inactiveTime = now - lastActivity;

            if (inactiveTime >= TIMEOUT_MS) {
                handleLogout();
            } else if (inactiveTime >= TIMEOUT_MS - WARNING_MS) {
                if (!showWarning) setShowWarning(true);
                setRemainingSeconds(Math.floor((TIMEOUT_MS - inactiveTime) / 1000));
            }
        }, 1000);

        return () => {
            events.forEach(event => window.removeEventListener(event, resetTimer));
            clearInterval(checkInterval);
        };
    }, [lastActivity, resetTimer, showWarning, handleLogout]);

    return (
        <Snackbar
            open={showWarning}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ mt: 8 }}
        >
            <Alert 
                severity="warning" 
                variant="filled"
                sx={{ width: '100%', minWidth: 400, boxShadow: 6 }}
                action={
                    <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleExtendSession} 
                        variant="outlined"
                        disabled={extending}
                    >
                        {extending ? 'Extending...' : 'Extend Session'}
                    </Button>
                }
            >
                <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                        Session Timeout Warning
                    </Typography>
                    <Typography variant="body2">
                        Your session will expire in {Math.floor(remainingSeconds / 60)}:{(remainingSeconds % 60).toString().padStart(2, '0')} due to inactivity.
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={(remainingSeconds / 600) * 100} 
                        sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} 
                    />
                </Box>
            </Alert>
        </Snackbar>
    );
}
